import React, { useState, useEffect, ChangeEvent, useCallback, useRef, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMst } from '../../model/Root';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import Layout from '../../components/Layout';
import { Audiobook, Chapter, MediaTypes } from '../../types';
import { color } from '../../theme';
import TextInput from '../../components/FormComponents/TextInput';
import Button from '../../components/Button/Button';
import { AudiobookDefaults } from '../../utils/defaults';
import AddNewButton from '../../components/Button/AddButton';
import { Grid, IconButton, Typography, Divider } from '@mui/material';
import EditIcon from '../../static/edit.svg';
import DeleteIcon from '../../static/delete.svg';
import SaveIcon from '../../static/save.svg';
import UploadButton from '../../components/Button/UploadButton';
import ImageUploadButton from '../../components/Button/ImageUploadButton';
import { Formik, FormikProps } from 'formik';
import FormFieldText from 'components/FormComponents/FormFieldText';
import FormFieldDate from 'components/FormComponents/FormFieldDate';
import FormFieldNumber from 'components/FormComponents/FormFieldNumber';
import FormFieldTextArea from 'components/FormComponents/FormFieldTextArea';
import FormFieldCheckboxList from 'components/FormComponents/FormFieldCheckboxList';
import FileUploadButton from 'components/Button/FileUploadButton';
import FormFieldSwitch from 'components/FormComponents/FormFieldSwitch';
import { getAudiobookValidationSchema } from 'utils/audiobookValidation';
import * as Yup from 'yup';
import AudiobookImportChaptersFromCsDialog from './AudiobookImportChaptersFromCsDialog';
import { getAudiobookDetailsForImport, recalculateAudiobookDuration } from 'services/audiobookApi';

const AudiobookEditScreen = observer(() => {
  const { t } = useTranslation();
  const { errorStore } = useMst();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    categoryStore: { categories, getCategories },
    tagStore: { tags, getTags },
    targetGroupStore: { targetGroups, getTargetGroups },
    audiobookStore: {
      selectedAudiobook,
      getSelectedAudiobook,
      updateAudiobook,
      createAudiobook,
      loading,
      selectedImportedAudiobook,
      clearImportedAudiobook,
    },
  } = useMst();
  const { id } = useParams<{ id: string }>();
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>();
  const [selectedImage, setSelectedImage] = useState<File>();
  const [selectedEbookFile, setSelectedEbookFile] = useState<File>();
  const [selectedPdfBookFile, setSelectedPdfBookFile] = useState<File>();
  const [currentChapterId, setCurrentChapterId] = useState(0);
  const [currentFiles, setCurrentFiles] = useState<File[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [audiobook, setAudiobook] = useState<Audiobook>(AudiobookDefaults);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [chapterName, setChapterName] = useState('');
  const [uploadDialogVisible, setUploadDialogVisible] = useState<boolean>(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const [csImportDialogOpen, setCsImportDialogOpen] = useState(false);
  const [recalculateLoading, setRecalculateLoading] = useState(false);
  const [autoImportCsId] = useState(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('importCsId');
    return raw && raw.length > 0 ? raw : null;
  });
  const autoImportHandledRef = useRef(false);

  const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds == null || Number.isNaN(seconds) || seconds < 0) return '—';

    const rounded = Math.round(seconds);
    const hours = Math.floor(rounded / 3600);
    const minutes = Math.floor((rounded % 3600) / 60);
    const secs = rounded % 60;

    const hh = hours.toString();
    const mm = minutes.toString().padStart(2, '0');
    const ss = secs.toString().padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  const normalizeNullableNumber = (value: unknown): number | null => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const chapterDurationSeconds = useMemo(
    () =>
      chapters
        .filter((chapter) => chapter.state !== 'deleted')
        .reduce((sum, chapter) => sum + (chapter.duration ?? 0), 0),
    [chapters, refreshPage]
  );

  const getAudioDurationSeconds = async (file: File): Promise<number | null> => {
    try {
      const objectUrl = URL.createObjectURL(file);
      const duration = await new Promise<number | null>((resolve) => {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        const timeoutMs = 10000;
        let settled = false;

        const timeoutId = window.setTimeout(() => {
          settle(null);
        }, timeoutMs);

        const cleanup = () => {
          window.clearTimeout(timeoutId);
          audio.onloadedmetadata = null;
          audio.onerror = null;
          audio.onabort = null;
          audio.removeAttribute('src');
          audio.load();
          URL.revokeObjectURL(objectUrl);
        };

        const settle = (value: number | null) => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(value);
        };

        audio.onloadedmetadata = () => {
          const d = audio.duration;
          settle(Number.isFinite(d) ? d : null);
        };
        audio.onerror = () => {
          settle(null);
        };
        audio.onabort = () => {
          settle(null);
        };

        audio.src = objectUrl;
      });

      return duration;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    const fetchLists = async () => {
      await getCategories(MediaTypes.Audiobook);
      await getTags(MediaTypes.Audiobook);
      await getTargetGroups();
    };

    fetchLists();
  }, [getCategories, getTags, getTargetGroups, categories, tags, targetGroups]);

  useEffect(() => {
    const fetchPage = async () => {
      if (id === 'create' && selectedImportedAudiobook) {
        const bookData = {
          id: 0,
          name: selectedImportedAudiobook.name ?? '',
          author: selectedImportedAudiobook.author ?? '',
          publisher: selectedImportedAudiobook.publisher ?? '',
          description: selectedImportedAudiobook.description ?? '',
          releaseDate: selectedImportedAudiobook.releaseDate ?? '',
          releaseYear: selectedImportedAudiobook.releaseYear ?? 0,
          language: selectedImportedAudiobook.language ?? 'fi',
          image: selectedImportedAudiobook.image ?? '',
          reader: selectedImportedAudiobook.reader ?? '',
          shopUrl: selectedImportedAudiobook.shopUrl ?? '',
          isbn: (selectedImportedAudiobook as unknown as { isbn?: string | null })?.isbn ?? '',
          ebook: selectedImportedAudiobook.ebook ?? '',
          pdfBook: selectedImportedAudiobook.pdfBook ?? '',
          mediaTypeId: 5,
        };
        setAudiobook((prev) => ({ ...(prev as Audiobook), ...(bookData as Audiobook) }));
        setImagePreviewUrl(selectedImportedAudiobook.image);
        const chapters =
          selectedImportedAudiobook.chapters?.map((c) => ({ ...c, state: 'new', id: 0, published: false })) ?? [];
        setChapters(chapters);
      } else if (id !== 'create') {
        getSelectedAudiobook(id as string).then(() => {
          if (selectedAudiobook?.image) {
            setImagePreviewUrl(selectedAudiobook?.image);
          }
          setAudiobook((prev) => ({ ...(prev as Audiobook), ...(selectedAudiobook as Audiobook) }));
          const chapters = selectedAudiobook?.chapters.map((c) => ({ ...c, state: '' })) ?? [];
          setChapters(chapters);
        });
      }
    };

    fetchPage();
  }, [selectedImportedAudiobook, getSelectedAudiobook, selectedAudiobook, id]);

  useEffect(() => {
    // This is the cleanup function that runs when the component is unmounted
    return () => {
      if (selectedImportedAudiobook) {
        clearImportedAudiobook();
      }
    };
  }, [selectedImportedAudiobook, clearImportedAudiobook]);

  const handleImageSelection = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (target?.files) {
      const image = URL.createObjectURL(target.files[0]);
      setSelectedImage(target.files[0]);
      setImagePreviewUrl(image);
    }
  };

  const handleChapterNameChange = (key: string, value: string) => {
    setChapterName(value);
  };

  const handleFilePick = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileArray = Array.from(e.target.files);
      setCurrentFiles(fileArray);

      if (e.target.files.length == 1) {
        if (chapterName == '') {
          const filenameWithoutExt = e.target.files[0].name.split('.').slice(0, -1).join('.');
          setChapterName(filenameWithoutExt);
        }
      } else {
        const existingChapters = chapters.filter((c) => c.state !== 'deleted');
        const baseChapterNumber =
          existingChapters.length === 0 ? 0 : (existingChapters[existingChapters.length - 1].chapterNumber ?? 0) + 1;

        const newChapters: Chapter[] = await Promise.all(
          fileArray.map(async (file, idx) => {
            const filenameWithoutExt = file.name.split('.').slice(0, -1).join('.');
            const duration = await getAudioDurationSeconds(file);
            return {
              name: filenameWithoutExt,
              file,
              chapterNumber: baseChapterNumber + idx,
              src: file.name.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
              duration,
              state: 'new',
            };
          })
        );

        setChapters([...chapters, ...newChapters]);
        setFiles([...files, ...fileArray]);

        setCurrentFiles([]);
        setChapterName('');
        setCurrentChapterId(0);
        setUploadDialogVisible(false);
      }
    }
  };

  const importChaptersFromCs = useCallback(
    async (csId: string) => {
      try {
        const response = (await getAudiobookDetailsForImport(csId)) as unknown as {
          data?: {
            chapters?: Array<{ name?: string; chapterNumber?: number; src?: string }>;
          };
        };
        const data =
          response?.data ??
          ({
            chapters: (
              response as unknown as {
                chapters?: Array<{ name?: string; chapterNumber?: number; src?: string }>;
              }
            )?.chapters,
          } as {
            chapters?: Array<{ name?: string; chapterNumber?: number; src?: string }>;
          });
        const importedChapters = (data.chapters ?? []).filter(
          (ch) => ch && typeof ch.src === 'string' && ch.src.length > 0
        );

        if (importedChapters.length === 0) {
          errorStore.setError('import_cs_audio_no_chapters');
          return false;
        }

        const deletedExisting: Chapter[] =
          chapters
            ?.filter((ch) => (ch.id ?? 0) > 0)
            .map((ch) => ({
              ...ch,
              state: 'deleted',
            })) ?? [];

        const newChapters: Chapter[] = importedChapters.map((ch, index) => ({
          id: 0,
          name: ch.name || `Luku ${typeof ch.chapterNumber === 'number' ? ch.chapterNumber : index + 1}`,
          src: ch.src || '',
          chapterNumber:
            typeof ch.chapterNumber === 'number' && !Number.isNaN(ch.chapterNumber) ? ch.chapterNumber : index,
          duration: (ch as unknown as { duration?: number | null })?.duration ?? null,
          state: 'new',
        }));

        setChapters([...deletedExisting, ...newChapters]);
        errorStore.setSuccess('import_cs_audio_success');
        return true;
      } catch (err) {
        console.error(err);
        errorStore.setError('import_cs_audio_failed');
        return false;
      }
    },
    [chapters, errorStore]
  );

  useEffect(() => {
    if (!autoImportCsId || autoImportHandledRef.current) return;
    autoImportHandledRef.current = true;
    void importChaptersFromCs(autoImportCsId);
  }, [autoImportCsId, importChaptersFromCs]);

  const handleChapterSave = async () => {
    if (currentChapterId == 0) return;

    if (currentFiles.length == 1) {
      // new chapter
      const duration = await getAudioDurationSeconds(currentFiles[0]);
      const existingChapters = chapters.filter((c) => c.state !== 'deleted');
      const nextChapterNumber =
        existingChapters.length === 0 ? 0 : (existingChapters[existingChapters.length - 1].chapterNumber ?? 0) + 1;

      const newChapter: Chapter = {
        name: chapterName,
        file: currentFiles[0],
        chapterNumber: nextChapterNumber,
        src: currentFiles[0].name.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
        duration,
        state: 'new',
      };

      setChapters([...chapters, newChapter]);
      setFiles([...files, currentFiles[0]]);
    } else {
      const index = chapters.findIndex((chapter) => chapter.id === currentChapterId);
      if (index > -1) {
        const updated = [...chapters];
        updated[index] = {
          ...updated[index],
          name: chapterName,
          state: 'edited',
        };
        setChapters(updated);
      }
    }
    setCurrentFiles([]);
    setChapterName('');
    setCurrentChapterId(0);
    setUploadDialogVisible(false);
  };

  const handleChapterEdit = (chapter: Chapter) => {
    setUploadDialogVisible(true);
    setCurrentChapterId(chapter.id ?? 0);

    const index = getChapterIndex(chapter);
    setChapterName(chapters[index].name);
  };

  const handleDeleteFile = (chapter: Chapter) => {
    const index = getChapterIndex(chapter);

    if (index > -1) {
      if ((chapters[index].id ?? 0) > 0) {
        // already saved to db, flag for delete
        chapters[index].state = 'deleted';
      } else {
        // just remove item from array, not stored to db
        chapters.splice(index, 1);
      }

      setRefreshPage(!refreshPage);
    }
  };

  const getChapterIndex = (chapter: Chapter): number => {
    const index = chapters.findIndex(
      (c) => c.id == chapter.id && c.name == chapter.name && c.chapterNumber == chapter.chapterNumber
    );
    return index;
  };

  const saveAudiobook = async (book: Audiobook) => {
    const isImport = !!selectedImportedAudiobook;
    const normalizedBook: Audiobook = {
      ...book,
      totalDurationOverrideSeconds: normalizeNullableNumber(book.totalDurationOverrideSeconds),
    };
    console.log('--- Saving Audiobook ---');
    console.log('Is Import:', isImport);
    console.log('Book Data:', JSON.stringify(normalizedBook, null, 2));
    console.log('Chapters:', JSON.stringify(chapters, null, 2));
    console.log(
      'New Files to Upload:',
      files.map((f) => f.name)
    );

    if (id !== 'create') {
      console.log('Executing updateAudiobook...');
      await updateAudiobook(normalizedBook, chapters, files, selectedImage, selectedEbookFile, selectedPdfBookFile);
    } else {
      const image = isImport && !selectedImage ? normalizedBook.image : selectedImage;
      const ebook = isImport && !selectedEbookFile ? normalizedBook.ebook : selectedEbookFile;
      const pdfBook = isImport && !selectedPdfBookFile ? normalizedBook.pdfBook : selectedPdfBookFile;

      console.log('Executing createAudiobook...');
      await createAudiobook(normalizedBook, chapters, files, image, ebook, pdfBook);
    }
    if (isImport) {
      clearImportedAudiobook();
    }
    console.log('--- Save Complete, Navigating... ---');
    navigate('/audiobooks');
  };

  const handleRecalculateDuration = async () => {
    if (!id || id === 'create') {
      errorStore.setError('audiobook_duration_recalculate_save_first');
      return;
    }

    setRecalculateLoading(true);
    try {
      await recalculateAudiobookDuration(id);
      errorStore.setSuccess('audiobook_duration_recalculated');
      window.location.reload();
    } catch (err) {
      console.error(err);
      errorStore.setError('audiobook_duration_recalculate_failed');
    } finally {
      setRecalculateLoading(false);
    }
  };

  const renderChapterUpload = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={9}>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            {t('chapters')}
          </Typography>
        </Grid>
        {id !== 'create' && (
          <Grid item xs={3} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
            <Button
              text={recalculateLoading ? 'audiobook_duration_recalculating' : 'audiobook_duration_recalculate'}
              small
              onClick={() => {
                void handleRecalculateDuration();
              }}
              marginTop={0}
              disabled={recalculateLoading}
            />
            <Button text="import_cs_audio_button" small onClick={() => setCsImportDialogOpen(true)} marginTop={0} />
          </Grid>
        )}
        {chapters.length == 0 && (
          <Grid item xs={9}>
            <Typography variant="body2" color="GrayText">
              {t('no_chapters')}
            </Typography>
          </Grid>
        )}

        {chapters
          .filter((c) => c.state != 'deleted')
          .map((chapter: Chapter, index: number) => {
            return (
              <Grid
                container
                key={`${chapter.id}_${index}`}
                style={{ borderBottom: '1px solid lightGray', marginLeft: 24 }}
              >
                <Grid item xs={9} alignSelf={'center'}>
                  <Typography>{chapter.name}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <IconButton onClick={() => handleChapterEdit(chapter)}>
                    <img src={EditIcon} alt="Edit" />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteFile(chapter)}>
                    <img src={DeleteIcon} alt="Delete" />
                  </IconButton>
                </Grid>
                <Grid item></Grid>
              </Grid>
            );
          })}
        <Grid item xs={12}>
          <AddNewButton
            text={t('add_chapter')}
            onClick={() => {
              setCurrentChapterId(-1);
              setChapterName('');
              setUploadDialogVisible(!uploadDialogVisible);
            }}
          />
        </Grid>
        {uploadDialogVisible && (
          <Grid
            container
            item
            xs={10}
            spacing={1}
            style={{ border: 'solid 1px grey', borderRadius: 3, padding: 10, margin: 10 }}
          >
            {currentFiles.length <= 1 && (
              <Grid item xs={12}>
                <TextInput
                  label={'chapter_name'}
                  value={chapterName}
                  type={'name'}
                  placeholder={'chapter_name'}
                  onChange={handleChapterNameChange}
                  style={{ width: '80%' }}
                  multiline={false}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              {currentChapterId == -1 ? (
                <UploadButton
                  buttonText="upload_chapter"
                  onChange={handleFilePick}
                  fileName={currentFiles?.length === 1 ? currentFiles[0].name : currentFiles.length > 0 ? 'Useita' : ''}
                  fileType=".mp3"
                />
              ) : (
                <Typography variant="caption" color="GrayText">
                  {t('audiobook_chapter_edit_limited')}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <IconButton onClick={handleChapterSave}>
                <img src={SaveIcon} alt="Save" />
              </IconButton>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderHeader = () => {
    const modeText = id !== 'create' ? t('audiobook_edit') : t('audiobook_add');
    return loading ? t('audiobook_loading') : modeText;
  };

  if (loading) {
    return (
      <Layout title={renderHeader()}>
        <CircularProgress size={40} style={{ alignSelf: 'center', marginTop: 30, color: color.primaryButton }} />
        <Typography variant="h5">{t('audiobook_loading_text')}</Typography>
      </Layout>
    );
  }

  return (
    <Formik
      initialValues={audiobook}
      enableReinitialize
      onSubmit={(values: Audiobook) => {
        saveAudiobook(values);
      }}
      validationSchema={Yup.lazy((values: Audiobook) => getAudiobookValidationSchema(t, values))}
    >
      {({ values, isValid, errors, submitForm, setFieldValue }: FormikProps<Audiobook>) => {
        const manualOverrideSeconds = normalizeNullableNumber(values.totalDurationOverrideSeconds);

        if (Object.keys(errors).length > 0) {
          console.log('--- Form Validation Errors ---', errors);
        }
        return (
          <Layout title={renderHeader()}>
            <Grid container spacing={4}>
              <Grid item xs={10}>
                <Typography
                  variant="h4"
                  color={audiobook?.published ? 'primary' : 'secondary'}
                  borderColor={audiobook?.published ? 'primary' : 'secondary'}
                  border={1}
                  borderRadius={2}
                  sx={{ padding: 1, fontSize: 18, width: 200, marginBottom: 2 }}
                >
                  {audiobook?.published ? t('published') : t('draft')}
                </Typography>
              </Grid>

              {/* Add/modify name, description, number and release date */}
              <Grid item xs={10}>
                <FormFieldText label={t('audiobook_name')} name="name" />
              </Grid>
              <Grid container item xs={10} spacing={3}>
                <Grid item xs={4}>
                  <FormFieldText label={t('audiobook_author')} name="author" />
                </Grid>
                <Grid item xs={4}>
                  <FormFieldText label={t('audiobook_publisher')} name="publisher" />
                </Grid>
                <Grid item xs={4}>
                  <FormFieldDate label={t('release_date')} name="releaseDate" />
                </Grid>
              </Grid>
              <Grid container item xs={10} spacing={3}>
                <Grid item xs={4}>
                  <FormFieldText label={t('audiobook_reader')} name="reader" />
                </Grid>
                <Grid item xs={4}>
                  <FormFieldText label={t('audiobook_language')} name="language" />
                </Grid>
                <Grid item xs={4}>
                  <FormFieldNumber label={t('audiobook_releaseYear')} name="releaseYear" />
                </Grid>
                <Grid item xs={4}>
                  <FormFieldText label={t('audiobook_isbn')} name="isbn" />
                </Grid>
              </Grid>
              <Grid container item xs={10} spacing={3}>
                <Grid item xs={4}>
                  <Typography variant="subtitle2">{t('audiobook_effective_total_duration')}</Typography>
                  <Typography variant="body1">
                    {formatDuration(values.totalDurationSeconds)}
                    {values.totalDurationSeconds != null ? ` (${Math.round(values.totalDurationSeconds)} s)` : ''}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2">{t('audiobook_calculated_total_duration')}</Typography>
                  <Typography variant="body1">
                    {formatDuration(values.calculatedTotalDurationSeconds)}
                    {values.calculatedTotalDurationSeconds != null
                      ? ` (${Math.round(values.calculatedTotalDurationSeconds)} s)`
                      : ''}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2">{t('audiobook_local_chapter_duration')}</Typography>
                  <Typography variant="body1">
                    {chapterDurationSeconds > 0
                      ? `${formatDuration(chapterDurationSeconds)} (${Math.round(chapterDurationSeconds)} s)`
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <FormFieldNumber
                    label={t('audiobook_duration_override_seconds')}
                    name="totalDurationOverrideSeconds"
                  />
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2" color="GrayText">
                    {t('audiobook_duration_override_help')}
                  </Typography>
                  <Typography variant="body2" color="GrayText">
                    {manualOverrideSeconds != null
                      ? `${t('audiobook_manual_override_in_use')}: ${formatDuration(manualOverrideSeconds)} (${manualOverrideSeconds} s)`
                      : t('audiobook_manual_override_not_set')}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container item xs={10} spacing={3}>
                <Grid item xs={12}>
                  <FormFieldTextArea label={t('audiobook_description')} name="description" rows={7} />
                </Grid>
                <Grid item xs={12}>
                  <FormFieldText label={t('audiobook_shop_url')} name="shopUrl" />
                </Grid>
                <Grid item xs={12}>
                  <FormFieldSwitch name="isTimeTracked" label={t('audiobook_track_time')} />
                </Grid>
              </Grid>

              {/* Upload cover image */}
              <Grid item xs={10}>
                <ImageUploadButton
                  text="page_image"
                  imagePreviewUrl={imagePreviewUrl}
                  handleImageSelection={handleImageSelection}
                />
              </Grid>

              {/* Upload audiobook chapters (MP3) */}
              <Grid container item xs={10} spacing={3} ml={3}>
                {renderChapterUpload()}
              </Grid>

              {/* Upload ebook file */}
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h5">{t('book_ebook')}</Typography>
                </Grid>
                <Grid item xs={10}>
                  <FileUploadButton
                    text="book_ebook_file"
                    idPrefix="abook"
                    src={values?.ebook}
                    mimeType="application/epub+zip"
                    handleSelection={(event) => {
                      if (event.target.files) {
                        setFieldValue('ebook', event.target.files[0].name);
                        setSelectedEbookFile(event.target.files[0]);
                      }
                    }}
                    handleRemove={() => {
                      setFieldValue('ebook', null);
                      setSelectedEbookFile(undefined);
                    }}
                    emptyText="ebook_not_set"
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormFieldNumber label={t('audiobook.ebookStartSkip')} name="ebookStartSkip" />
                </Grid>
                <Grid item xs={3}>
                  <FormFieldNumber label={t('audiobook.ebookEndSkip')} name="ebookEndSkip" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" color="GrayText">
                    {t('audiobook.ebookStartSkipHelpText')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" color="GrayText">
                    {t('audiobook.ebookEndSkipHelpText')}
                  </Typography>
                </Grid>
              </Grid>

              {/* Upload pdf book file */}
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h5">{t('book_pdfBook')}</Typography>
                </Grid>
                <Grid item xs={10}>
                  <FileUploadButton
                    text="book_pdfBook_file"
                    idPrefix="pdfbook"
                    src={values?.pdfBook}
                    mimeType="application/pdf"
                    handleSelection={(event) => {
                      if (event.target.files) {
                        setFieldValue('pdfBook', event.target.files[0].name);
                        setSelectedPdfBookFile(event.target.files[0]);
                      }
                    }}
                    handleRemove={() => {
                      setFieldValue('pdfBook', null);
                      setSelectedPdfBookFile(undefined);
                    }}
                    emptyText="pdfBook_not_set"
                  />
                </Grid>
              </Grid>

              {/* Select categories, tags, user groups */}
              <Grid container item xs={12} spacing={3}>
                <Grid container item xs={6} spacing={3} alignContent={'flex-start'}>
                  <Grid item xs={12}>
                    <FormFieldCheckboxList label={t('categories')} name="categoryIds" listItems={categories} />
                  </Grid>

                  <Grid item xs={12}>
                    <FormFieldCheckboxList label={t('target_groups')} name="targetGroupIds" listItems={targetGroups} />
                  </Grid>

                  {/* publish or save as draft */}
                  <Grid container item xs={10} spacing={3}>
                    <Button
                      text="save_draft"
                      onClick={() => {
                        setFieldValue('published', false);
                        submitForm();
                      }}
                      backgroundColor={color.secondaryButton}
                      width={220}
                      marginBottom={30}
                    />
                    <Button
                      text="publish"
                      onClick={() => {
                        setFieldValue('published', true);
                        submitForm();
                      }}
                      marginBottom={30}
                      disabled={!isValid}
                    />
                    <Button
                      text="cancel"
                      onClick={() => {
                        if (selectedImportedAudiobook) {
                          clearImportedAudiobook();
                        }
                        navigate('/audiobooks');
                      }}
                      backgroundColor={color.error}
                      width={220}
                      marginBottom={30}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={6} spacing={3}>
                  <Grid item xs={12}>
                    <FormFieldCheckboxList label={t('tags')} nameField="tag" name="tagIds" listItems={tags} />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <FormFieldSwitch name="isTimeTracked" label={t('audiobook_track_time')} />
              </Grid>
              <Grid item xs={12} mt={3}>
                <Divider />
              </Grid>
            </Grid>
            <AudiobookImportChaptersFromCsDialog
              open={csImportDialogOpen}
              onClose={() => setCsImportDialogOpen(false)}
              onSelect={(csId: string) => {
                void importChaptersFromCs(csId);
              }}
            />
          </Layout>
        );
      }}
    </Formik>
  );
});

export default AudiobookEditScreen;
